import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
    History, CheckCircle, XCircle, Target, TrendingUp, Brain, RefreshCw,
    ChevronRight, Activity, Award, Gauge, BarChart2, Loader2, Zap
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    BarChart, Bar, Cell
} from 'recharts';

export default function PredictionHistory() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [retraining, setRetraining] = useState(false);
    const [accuracyTrend, setAccuracyTrend] = useState(null);
    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        try {
            const [histRes, trendRes] = await Promise.all([
                API.get('/history'),
                API.get('/model/accuracy-trend').catch(() => ({ data: null })),
            ]);
            setData(histRes.data);
            setAccuracyTrend(trendRes.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleRetrainAll = async () => {
        setRetraining(true);
        try {
            const res = await API.post('/model/retrain-all');
            toast.success(`Retrained ${res.data.total_retrained} matches`);
            fetchData();
        } catch (e) { toast.error('Retrain failed'); }
        setRetraining(false);
    };

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="glass-card p-12 text-center">
                    <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
            </div>
        );
    }

    const predictions = data?.predictions || [];
    const completed = predictions.filter(p => p.status === 'completed');
    const upcoming = predictions.filter(p => p.status !== 'completed');
    const accuracy = data?.accuracy || 0;
    const correct = data?.correct || 0;
    const analyzed = data?.analyzed || 0;

    const trendData = accuracyTrend?.trend?.map((t, i) => ({
        name: `M${i + 1}`,
        accuracy: t.accuracy,
        total: t.total,
        correct: t.correct,
    })) || [];

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5" data-testid="prediction-history-page">
            {/* Header */}
            <div className="glass-card p-5 border-l-4 border-l-amber-500">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <History className="w-5 h-5 text-amber-400" />
                        <div>
                            <h1 className="heading-primary text-base md:text-xl text-white">PREDICTION HISTORY</h1>
                            <div className="text-xs text-white/30 mt-0.5">IPL 2026 — Predicted vs Actual Results</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={handleRetrainAll} disabled={retraining} size="sm"
                            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white text-xs"
                            data-testid="retrain-all-btn">
                            {retraining ? (
                                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Retraining...</>
                            ) : (
                                <><Brain className="w-3.5 h-3.5 mr-1.5" /> Retrain All Completed</>
                            )}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={fetchData} className="text-white/30 hover:text-white">
                            <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="glass-card p-4 text-center border border-white/5" data-testid="stat-total">
                    <div className="text-[10px] text-white/30 tech-mono">TOTAL PREDICTIONS</div>
                    <div className="text-2xl font-bold text-white mt-1">{predictions.length}</div>
                </div>
                <div className="glass-card p-4 text-center border border-white/5" data-testid="stat-analyzed">
                    <div className="text-[10px] text-white/30 tech-mono">MATCHES ANALYZED</div>
                    <div className="text-2xl font-bold text-cyan-400 mt-1">{analyzed}</div>
                </div>
                <div className="glass-card p-4 text-center border border-white/5" data-testid="stat-correct">
                    <div className="text-[10px] text-white/30 tech-mono">CORRECT</div>
                    <div className="text-2xl font-bold text-green-400 mt-1">{correct}</div>
                </div>
                <div className={`glass-card p-4 text-center border ${accuracy >= 60 ? 'border-green-500/20' : accuracy >= 40 ? 'border-yellow-500/20' : 'border-red-500/20'}`} data-testid="stat-accuracy">
                    <div className="text-[10px] text-white/30 tech-mono">ACCURACY</div>
                    <div className={`text-2xl font-bold mt-1 ${accuracy >= 60 ? 'text-green-400' : accuracy >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {accuracy}%
                    </div>
                </div>
            </div>

            {/* Accuracy Trend Chart */}
            {trendData.length > 0 && (
                <div className="glass-card p-5 border border-white/5" data-testid="accuracy-trend">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-4 h-4 text-cyan-400" />
                        <h3 className="heading-primary text-sm text-white">ACCURACY TREND</h3>
                        <span className="text-[10px] text-white/20 tech-mono ml-auto">IMPROVES WITH EACH MATCH</span>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                            <XAxis dataKey="name" tick={{ fill: '#555', fontSize: 10 }} />
                            <YAxis domain={[0, 100]} tick={{ fill: '#555', fontSize: 10 }} />
                            <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8, fontSize: 11 }} />
                            <Area type="monotone" dataKey="accuracy" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.15} strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Completed Matches — Predicted vs Actual */}
            {completed.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2 px-1">
                        <Award className="w-4 h-4 text-green-400" />
                        <span className="heading-primary text-sm text-white">COMPLETED — PREDICTED VS ACTUAL</span>
                        <span className="text-[10px] text-white/20 tech-mono">{completed.length} matches</span>
                    </div>
                    <div className="space-y-2">
                        {completed.map((p, i) => {
                            const home = p.match?.home_team || {};
                            const away = p.match?.away_team || {};
                            const isCorrect = p.correct === true;
                            const isWrong = p.correct === false;
                            return (
                                <div key={p.match_id || i}
                                    onClick={() => navigate(`/vimal?match=${p.match_id}`)}
                                    className={`glass-card p-4 cursor-pointer hover:bg-white/[0.06] transition-all border ${
                                        isCorrect ? 'border-green-500/20' : isWrong ? 'border-red-500/20' : 'border-white/5'
                                    }`}
                                    data-testid={`completed-prediction-${i}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="flex items-center gap-3">
                                            {isCorrect && <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />}
                                            {isWrong && <XCircle className="w-5 h-5 text-red-400 shrink-0" />}
                                            {p.correct === null && <Target className="w-5 h-5 text-white/20 shrink-0" />}
                                            <div>
                                                <div className="text-sm font-bold text-white">
                                                    {home.short_name || '?'} vs {away.short_name || '?'}
                                                </div>
                                                <div className="text-[10px] text-white/30 tech-mono">
                                                    {p.date?.slice(0, 10) || ''}
                                                    {p.home_score && ` | ${home.short_name} ${p.home_score} - ${away.short_name} ${p.away_score}`}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <div className="text-[10px] text-white/30 tech-mono">PREDICTED</div>
                                                <div className="text-sm font-bold text-cyan-400">{p.predicted_winner || '?'}</div>
                                                <div className="text-[10px] text-white/20">{p.home_win_pct}% - {p.away_win_pct}%</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[10px] text-white/30 tech-mono">ACTUAL</div>
                                                <div className={`text-sm font-bold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                                                    {p.actual_winner || 'N/A'}
                                                </div>
                                                {p.prediction_error !== undefined && (
                                                    <div className="text-[10px] text-white/20">Error: {p.prediction_error}%</div>
                                                )}
                                            </div>
                                            <div className="text-right min-w-[80px]">
                                                <div className="text-[10px] text-white/30 tech-mono">SOURCE</div>
                                                <div className="text-[10px] text-purple-400 font-medium">
                                                    {p.prediction_source === 'vimal_dashboard' ? 'Vimal AI' :
                                                     p.prediction_source === 'god_mode' ? 'God Mode' :
                                                     p.prediction_source === 'auto_prediction' ? 'Auto AI' :
                                                     p.prediction_source || 'AI Engine'}
                                                </div>
                                                {p.gpt_predicted_winner && (
                                                    <div className="text-[9px] text-violet-400/60">GPT: {p.gpt_predicted_winner}</div>
                                                )}
                                                {p.external_predicted_winner && (
                                                    <div className="text-[9px] text-yellow-400/60">Intel: {p.external_predicted_winner}</div>
                                                )}
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-white/15" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Upcoming Predictions */}
            {upcoming.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2 px-1">
                        <Zap className="w-4 h-4 text-yellow-400" />
                        <span className="heading-primary text-sm text-white">UPCOMING PREDICTIONS</span>
                        <span className="text-[10px] text-white/20 tech-mono">{upcoming.length} matches</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {upcoming.slice(0, 10).map((p, i) => {
                            const home = p.match?.home_team || {};
                            const away = p.match?.away_team || {};
                            return (
                                <div key={p.match_id || i}
                                    onClick={() => navigate(`/vimal?match=${p.match_id}`)}
                                    className="glass-card p-4 cursor-pointer hover:bg-white/[0.06] transition-all border border-white/5"
                                    data-testid={`upcoming-prediction-${i}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-bold text-white">
                                                {home.short_name || '?'} vs {away.short_name || '?'}
                                            </div>
                                            <div className="text-[10px] text-white/30 tech-mono">{p.date?.slice(0, 10)}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <div className="text-sm font-bold text-cyan-400">{p.predicted_winner || '?'}</div>
                                                <div className="text-[10px] text-white/20">{p.home_win_pct}% - {p.away_win_pct}%</div>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-white/15" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {predictions.length === 0 && (
                <div className="glass-card p-12 text-center border border-white/5">
                    <History className="w-8 h-8 text-white/10 mx-auto mb-3" />
                    <div className="text-sm text-white/30">No predictions generated yet</div>
                    <div className="text-xs text-white/15 mt-1">
                        Predictions will populate once matches start or after generating batch predictions from Admin Panel
                    </div>
                    <Button onClick={() => navigate('/admin')} size="sm" className="mt-4 bg-amber-600 hover:bg-amber-500 text-white text-xs">
                        Go to Admin Panel
                    </Button>
                </div>
            )}

            {/* Footer */}
            <div className="text-center tech-mono text-[10px] text-white/15">
                V-One AI | IPL 2026 | Model continuously improves with each match
            </div>
        </div>
    );
}
